/**
 * score-frame.ts — per-frame scoring verdict (Phase 7 scoring, task #2).
 *
 * `scoreFrame` maps one raw `NativeF0Frame` onto the target note active at a given
 * song-timeline position and returns a deterministic verdict. It is pure: same
 * (frame, melody, atMs) ⇒ same result, no React/native dependency.
 *
 * Invariants honoured here (GOAL.md "NEVER VIOLATE"):
 *   - Scoring uses the frame's RAW f0 (`frame.f0Hz`) — never the rounded
 *     `frame.midi` and never a display-smoothed value.
 *   - silent (no target, not singing) vs missed (target active, not singing) are
 *     distinct verdicts.
 *   - `atMs` is the SONG-timeline position for this frame. The capture→song
 *     mapping (latency / playbackAnchor, see docs/native/native-timebase-and-latency.md
 *     §4) is owned by the native/scoring layer and applied UPSTREAM, so the pure
 *     core never needs the native clock and stays deterministic.
 */

import type { TargetMelody, TargetNote } from './target-melody';
import type { NativeF0Frame } from '../types/pitch-tracker';

export type FrameVerdict =
  | 'in_tune'
  | 'high'
  | 'low'
  | 'silent'
  | 'missed'
  | 'unknown';

export type FrameScore = {
  /** MIDI of the target note active at `atMs`, or null when no note is active. */
  targetMidi: number | null;
  /** Signed cents the raw f0 is off the target (+sharp/−flat), or null. */
  centsDiff: number | null;
  verdict: FrameVerdict;
};

export type ScoreFrameOptions = {
  /** |cents| ≤ this ⇒ in_tune; default a half-semitone bucket (50¢). */
  centsTolerance: number;
  /** Voiced frames below this confidence are untrusted ⇒ unknown. */
  minConfidence: number;
  /**
   * Octave-invariant scoring: compare the raw f0 against the target's pitch
   * CLASS (same note name, any octave) by folding `centsDiff` to the target
   * octave nearest the user's f0 (±1200¢ steps ⇒ |cents| ≤ 600). Default true —
   * singing/detecting the right note one octave off then reads as in_tune, not a
   * −1200¢ "low". Set false for strict same-octave comparison.
   */
  octaveTolerant: boolean;
};

export const DEFAULT_SCORE_FRAME_OPTIONS: ScoreFrameOptions = {
  centsTolerance: 50,
  minConfidence: 0.5,
  octaveTolerant: true,
};

const midiToFreq = (midi: number): number =>
  440 * Math.pow(2, (midi - 69) / 12);

/** Signed cents of `f0Hz` relative to the target MIDI pitch (+sharp / −flat). */
const centsOff = (f0Hz: number, targetMidi: number): number =>
  1200 * Math.log2(f0Hz / midiToFreq(targetMidi));

/** Fold cents to the nearest octave of the target ⇒ pitch-class distance in [−600, 600]. */
const foldToNearestOctave = (cents: number): number =>
  cents - 1200 * Math.round(cents / 1200);

/** First note whose [startMs, startMs+durMs) span contains `atMs` (notes sorted). */
const activeNote = (melody: TargetMelody, atMs: number): TargetNote | null => {
  for (const note of melody.notes) {
    if (atMs < note.startMs) break; // sorted by startMs → no later note can match
    if (atMs < note.startMs + note.durMs) return note;
  }
  return null;
};

/** True when the frame carries a usable raw pitch measurement (not silence). */
const hasPitch = (
  frame: NativeF0Frame,
): frame is NativeF0Frame & { f0Hz: number } =>
  frame.voiced &&
  frame.f0Hz !== null &&
  Number.isFinite(frame.f0Hz) &&
  frame.f0Hz > 0;

/**
 * Score a single raw frame against the melody at song-time `atMs`. Pure/deterministic.
 */
export const scoreFrame = (
  frame: NativeF0Frame,
  melody: TargetMelody,
  atMs: number,
  opts: ScoreFrameOptions = DEFAULT_SCORE_FRAME_OPTIONS,
): FrameScore => {
  const note = activeNote(melody, atMs);

  // No target note active (rest / before-start / after-end): nothing to score.
  if (note === null) {
    return { targetMidi: null, centsDiff: null, verdict: 'silent' };
  }

  // Target active but the user is not singing → missed (distinct from silent).
  if (!hasPitch(frame)) {
    return { targetMidi: note.midi, centsDiff: null, verdict: 'missed' };
  }

  // Singing but the detector isn't confident → can't judge tune.
  if (frame.confidence < opts.minConfidence) {
    return { targetMidi: note.midi, centsDiff: null, verdict: 'unknown' };
  }

  const rawCents = centsOff(frame.f0Hz, note.midi);
  const centsDiff = opts.octaveTolerant
    ? foldToNearestOctave(rawCents)
    : rawCents;
  const verdict: FrameVerdict =
    Math.abs(centsDiff) <= opts.centsTolerance
      ? 'in_tune'
      : centsDiff > 0
        ? 'high'
        : 'low';

  return { targetMidi: note.midi, centsDiff, verdict };
};
